import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { guardianshipsService, type HeldWard } from '../../services/guardianships';
import type { EnteredAthlete } from './EnteredAthletes';
import type { StageParticipation } from '../../types/trophy';
import type { CompetitionRound } from '../../types/competition-round';

/**
 * The children this account holds, for the pages that have to draw them
 * without being asked — the circuit's stage cards and a stage's round cards,
 * where a parent looks for the child they entered.
 *
 * Nothing is fetched for a visitor: the endpoint needs an account, and a page
 * that is mostly read by people with no children should not spend a 401 on it.
 * A failure is read as "no children" rather than an error on a page about
 * something else.
 */
export function useGuardianWards() {
  const { user } = useAuth();
  const [wards, setWards] = useState<HeldWard[]>([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!user) {
      setWards([]);
      return;
    }

    setLoading(true);
    try {
      setWards(await guardianshipsService.wards());
    } catch {
      setWards([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { wards, loading, reload };
}

/** The children of this account who are on one competition's start list. */
export function wardsEnteredIn(wards: HeldWard[], competitionSlug: string): EnteredAthlete[] {
  return wards.flatMap((ward) =>
    ward.entries
      .filter((entry) => entry.competition_slug === competitionSlug)
      .map((entry) => ({
        entryId: entry.id,
        userId: ward.id,
        fullName: ward.full_name,
        name: ward.name,
        surname: ward.surname,
        avatarColor: ward.avatar_color,
        categoryId: entry.category_id ?? null,
        categoryName: entry.category_name ?? null,
        roundId: entry.competition_round_id ?? null,
        roundName: entry.competition_round_name ?? null,
        turnToChoose: !!entry.turn_to_choose,
        turns: entry.turns ?? [],
        paid: entry.paid,
        competitionName: entry.competition_name,
      }))
  );
}

/** The viewer's own entry at a stage, when they have one, in the same shape. */
export function viewerEnteredIn(
  participation: StageParticipation | null | undefined,
  viewer: { name?: string | null; surname?: string | null; full_name?: string; email?: string;
            avatar_color?: string | null; avatar_small_url?: string | null; avatar_url?: string | null } | null
): EnteredAthlete[] {
  if (!participation?.entered || !participation.entry || !viewer) return [];

  return [
    {
      entryId: participation.entry.id,
      // Null is what tells the withdrawal endpoint this is the viewer's own.
      userId: null,
      fullName: viewer.full_name || viewer.email || '',
      name: viewer.name,
      surname: viewer.surname,
      avatarColor: viewer.avatar_color ?? null,
      avatarUrl: viewer.avatar_small_url ?? viewer.avatar_url ?? null,
      categoryId: participation.entry.category_id ?? null,
      categoryName: participation.entry.category_name ?? null,
      roundId: participation.entry.competition_round_id ?? null,
      roundName: participation.entry.competition_round_name ?? null,
      turnToChoose: !!participation.entry.turn_to_choose,
      turns: participation.entry.turns ?? [],
      paid: participation.entry.paid,
      competitionName: participation.name,
    },
  ];
}

/**
 * The ones a round's card shows: a round runs named classes, so an entrant
 * belongs in the card of theirs — and when the round is a turn, only if they
 * booked it or have not booked any, the same rule as the round's board
 * (CompetitionParticipant.climbing_in on the server).
 *
 * Except somebody with a turn still to choose: the boards hold them on every
 * turn (a judge must never miss them), but on this page they sit in the box
 * above the rounds, where the choice is made (see `withTurnToChoose`), rather
 * than in every turn's card at once.
 */
export function inRound(
  athletes: EnteredAthlete[],
  round: Pick<CompetitionRound, 'id' | 'category_ids' | 'bookable'>
): EnteredAthlete[] {
  return athletes.filter(
    (athlete) =>
      !!athlete.categoryId &&
      round.category_ids.includes(athlete.categoryId) &&
      (!round.bookable || (!athlete.roundId && !athlete.turnToChoose) || athlete.roundId === round.id)
  );
}

/**
 * The ones whose class is split over two or more turns and who have booked
 * none: the stage's page lists them above the rounds, with the way to choose.
 */
export function withTurnToChoose(athletes: EnteredAthlete[]): EnteredAthlete[] {
  return athletes.filter((athlete) => !!athlete.categoryId && !!athlete.turnToChoose);
}

/**
 * The ones no round's card can show, because the desk has not sorted them into
 * a class yet — which is why the stage's page lists them above the rounds
 * instead, where the entry can still be taken back.
 */
export function withoutCategory(athletes: EnteredAthlete[]): EnteredAthlete[] {
  return athletes.filter((athlete) => !athlete.categoryId);
}

/**
 * Whether this account still holds somebody who could be entered here — what
 * puts an "enter another athlete" button beside a parent's own tick.
 */
export function hasWardToEnter(wards: HeldWard[], competitionSlug: string): boolean {
  return wards.some((ward) => !ward.entries.some((entry) => entry.competition_slug === competitionSlug));
}
